import { useMemo, useState } from 'react';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import {
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import { WALLET_PHRASES, WALLET_REMOVE_DIALOG } from '@/consts/phrases/walletPhrases';
import { IWalletUploadedDocument } from '@/types/types/forms/wallet/wallet.interface';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FileIcon } from '@/assets/theme/default/general/FileIcon';

interface WalletUploadedFilesListProps {
    documents?: Record<string, IWalletUploadedDocument[]>;
    handleDocumentRemove?: (id: string, file: IWalletUploadedDocument) => void;
}

interface IWalletUploadedDocumentWithOriginalId extends IWalletUploadedDocument {
    originalId: string;
}

export const WalletUploadedFilesList = ({
    documents,
    handleDocumentRemove,
}: WalletUploadedFilesListProps) => {
    const { t } = usePhrase();
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [documentToRemove, setDocumentToRemove] = useState<{
        id: string;
        document: IWalletUploadedDocument;
    } | null>(null);

    const groupedDocuments = useMemo(() => {
        if (!documents) return {};

        const grouped: Record<string, IWalletUploadedDocumentWithOriginalId[]> = {};

        Object.entries(documents).forEach(([id, docs]) => {
            docs.forEach((doc) => {
                const key = doc.fileFieldPhraseId?.toString() || 'default';
                if (!grouped[key]) {
                    grouped[key] = [];
                }
                grouped[key].push({ ...doc, originalId: id });
            });
        });

        return grouped;
    }, [documents]);

    const handleRemoveClick = (document: IWalletUploadedDocumentWithOriginalId) => {
        setDocumentToRemove({ id: document.originalId, document });
        setRemoveDialogOpen(true);
    };

    const handleDialogClose = (confirmed?: boolean) => {
        setRemoveDialogOpen(false);
        if (confirmed && documentToRemove && handleDocumentRemove) {
            const { originalId: _, ...docWithoutOriginalId } =
                documentToRemove.document as IWalletUploadedDocumentWithOriginalId;
            handleDocumentRemove(documentToRemove.id, docWithoutOriginalId);
        }
        setDocumentToRemove(null);
    };

    if (!documents || Object.keys(documents).length === 0) {
        return null;
    }

    return (
        <>
            {Object.entries(groupedDocuments).map(([phraseId, docs]) => (
                <Grid size={12} key={phraseId}>
                    {phraseId !== 'default' && (
                        <Typography variant="greyLabel">
                            {t(parseInt(phraseId), { asString: true })}
                        </Typography>
                    )}
                    {docs.map((document, index) => (
                        <Stack
                            key={`${phraseId}-${index}`}
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <FileIcon />
                            {document.file.name}
                            {handleDocumentRemove && (
                                <Tooltip title={t(WALLET_PHRASES.REMOVE_DOCUMENT)}>
                                    <IconButton
                                        aria-label="remove"
                                        sx={{ padding: '4px' }}
                                        onClick={() => handleRemoveClick(document)}
                                    >
                                        <DeleteOutlineIcon
                                            sx={{ fontSize: '18px' }}
                                            color="error"
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    ))}
                </Grid>
            ))}
            {handleDocumentRemove && (
                <Dialog onClose={() => handleDialogClose()} open={removeDialogOpen}>
                    <DialogTitle variant="h4">
                        {t(WALLET_REMOVE_DIALOG.REMOVE_DIALOG_TITLE)}
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <DialogContentText>
                            {documentToRemove?.document.file.name &&
                                t(WALLET_REMOVE_DIALOG.REMOVE_DIALOG_BODY, {
                                    replaceTextVars: [documentToRemove.document.file.name],
                                })}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={() => handleDialogClose()}>
                            {t(WALLET_REMOVE_DIALOG.CANCEL)}
                        </Button>
                        <Button variant="contained" onClick={() => handleDialogClose(true)}>
                            {t(WALLET_REMOVE_DIALOG.REMOVE)}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};
